import React, {
    Children,
    useEffect,
    useMemo,
    useRef,
    useState,
    forwardRef,
    useImperativeHandle
} from 'react'
import {Box, HStack, Button, Flex, useSlotRecipe} from '@chakra-ui/react3'
import PropTypes from 'prop-types'
import {useIntl} from 'react-intl'
import useResizeObserver from '../../../../hooks/use-resize-observer'
import useCssVarValue from '../../../hooks/useCssVarValue'
import useAnimationFrameCallback from '../../../hooks/useAnimationFrameCallback'
import {ArrowLeftIcon, ArrowRightIcon} from '../../icons'
import {sliderSlotRecipe} from './index.recipe'
import useEffectEvent from '../../../../hooks/use-effect-event'

/**
 * Finds an element with layout (visible and measurable) by checking the element itself first,
 * then recursively searching children to find the widest visible element.
 * Used to calculate item width when item contains nested elements with display:contents.
 *
 * @param {HTMLElement|null} el - The element to search within
 * @returns {HTMLElement|null} The element if visible, otherwise the widest visible descendant, or null if none found
 *
 * Why recursive: When using display:contents, the element does not have layout
 * (offsetWidth = 0), so we need to find a descendant that does have layout.
 * checkVisibility() returns false for elements with display:none or display:contents.
 */
const getLayoutElement = (el) => {
    if (!el) return null
    if (!el.checkVisibility || el.checkVisibility()) return el
    // get the widest child that is visible
    return Array.from(el.children).reduce((widestChild, child) => {
        const layoutChild = getLayoutElement(child)
        if (!widestChild || layoutChild.offsetWidth > widestChild.offsetWidth) return layoutChild
        return widestChild
    }, null)
}

const Slider = forwardRef(function Slider(
    {children, variant, sliderLabel, sliderId, onIndexChange},
    ref
) {
    const {formatMessage} = useIntl()
    const recipe = useSlotRecipe({recipe: sliderSlotRecipe})
    const styles = useMemo(() => recipe({variant}), [recipe, variant])

    // need to use state to force rerender to trigger the resize observer and css var value
    const [containerEl, setContainerEl] = useState(null)
    const firstSlideRef = useRef(null)

    const count = Children.count(children)

    const [itemsPerSlide = 1] = useCssVarValue('--items-per-slide', {
        element: containerEl || null,
        initialValue: 1,
        parser: (value) => (value === '' ? 1 : parseFloat(value))
    })

    const indicatorNumber = Math.ceil(count - itemsPerSlide + 1)
    const maxIndex = Math.max(0, count - itemsPerSlide)
    const [current, setCurrent] = useState(0)
    const canPrev = current > 0
    const canNext = current < maxIndex

    const [stepWidth, setStepWidth] = useState(0)
    // RAF callback ensures layout reads (getComputedStyle, offsetWidth) happen at optimal frame timing,
    // avoiding forced synchronous layout and batching multiple resize events into single read per frame
    const doUpdateStepWidth = useAnimationFrameCallback(() => {
        if (!containerEl) return 0
        const cs = window.getComputedStyle(containerEl)
        const raw = cs.columnGap || cs.gap || '0'
        const val = parseFloat(raw)
        const gapX = Number.isFinite(val) ? val : 0

        const slideW = getLayoutElement(firstSlideRef.current)?.offsetWidth || 0

        setStepWidth(slideW + gapX)
    })
    useResizeObserver(containerEl, doUpdateStepWidth, {debounceMs: 50})
    // update step width when count changes - handles initial render without children
    useEffect(() => {
        if (count > 0 && stepWidth <= 0) {
            doUpdateStepWidth()
        }
    }, [count, stepWidth, doUpdateStepWidth])

    const clampIndex = (i) => Math.max(0, Math.min(i, maxIndex))

    const scrollToIndex = (i, behavior = 'smooth') => {
        if (!containerEl || stepWidth <= 0) return
        const target = i * stepWidth
        containerEl.scrollTo({left: target, behavior})
    }

    const prev = useEffectEvent(() => {
        const i = clampIndex(current - 1)
        scrollToIndex(i, 'smooth')
    })

    const next = useEffectEvent(() => {
        const i = clampIndex(current + 1)
        scrollToIndex(i, 'smooth')
    })

    const setCurrentIndex = useEffectEvent((i, options) => {
        scrollToIndex(clampIndex(i), options?.behavior ?? 'smooth')
    })

    useImperativeHandle(
        ref,
        () => ({
            setCurrentIndex,
            prev,
            next
        }),
        [setCurrentIndex, prev, next]
    )

    // Scroll handler: RAF ensures optimal timing, syncs state with actual position
    const updateCurrentFromScroll = useAnimationFrameCallback(() => {
        if (stepWidth <= 0) return
        if (!containerEl) return

        const i = Math.round(containerEl.scrollLeft / stepWidth)
        const nextIndex = Math.max(0, Math.min(i, Math.max(0, count - itemsPerSlide)))
        setCurrent(nextIndex)
        onIndexChange?.(nextIndex)
    })

    // update current item from scroll event
    useEffect(() => {
        if (!containerEl) return

        containerEl.addEventListener('scroll', updateCurrentFromScroll, {passive: true})

        return () => {
            containerEl.removeEventListener('scroll', updateCurrentFromScroll)
        }
    }, [containerEl, updateCurrentFromScroll])

    // update current item when items count, items per slide or step width changes
    useEffect(() => {
        updateCurrentFromScroll()
    }, [count, itemsPerSlide, stepWidth, updateCurrentFromScroll])

    return (
        <Flex
            role="region"
            aria-roledescription="carousel"
            id={'slider-' + sliderId}
            aria-label={sliderLabel}
            {...styles.sliderContainer}
        >
            {/* Controls */}
            <Flex {...styles.controlsContainer}>
                <Flex {...styles.buttonGroup}>
                    <Button
                        {...styles.navButton}
                        aria-controls={'slider-' + sliderId}
                        aria-label={formatMessage({
                            id: 'global.gallery.scroll_prev',
                            defaultMessage: 'Scroll previous'
                        })}
                        onClick={prev}
                        disabled={!canPrev}
                    >
                        <ArrowLeftIcon />
                    </Button>
                    <Box
                        // on mobile chrome the label doesn't update, although the button's disabled state does update
                        // so use key to force recreate of the element when current or count changes
                        key={`${current}/${count}`}
                        {...styles.cockpitPagination}
                    >
                        {String(current + 1).padStart(2, '0')}/{String(count).padStart(2, '0')}
                    </Box>
                    <Button
                        {...styles.navButton}
                        aria-controls={'slider-' + sliderId}
                        aria-label={formatMessage({
                            id: 'global.gallery.scroll_next',
                            defaultMessage: 'Scroll next'
                        })}
                        onClick={next}
                        disabled={!canNext}
                    >
                        <ArrowRightIcon />
                    </Button>
                </Flex>
                <HStack {...styles.pagination}>
                    {Array.from({length: indicatorNumber}).map((_, i) => (
                        <Box
                            key={i}
                            {...(i === current && {'data-current': true})}
                            h="1px"
                            w={{base: '16px', _current: '36px'}}
                            bg={{
                                base: 'background.actions.on-media.carousel-ticker',
                                _current: 'background.actions.on-media.active'
                            }}
                            borderRadius="full"
                            transition="all 0.3s ease"
                        />
                    ))}
                </HStack>
            </Flex>

            <Flex
                ref={setContainerEl}
                className="slides"
                scrollSnapType="x mandatory"
                scrollBehavior="smooth"
                {...styles.scrollContainer}
            >
                {Children.map(children, (child, idx) => {
                    const isVisible = idx >= current && idx < current + itemsPerSlide
                    return (
                        <Box
                            key={idx}
                            id={sliderId + '-' + idx}
                            ref={idx === 0 ? firstSlideRef : null}
                            data-testid="slide"
                            scrollSnapAlign="start"
                            scrollSnapStop="always"
                            role="group"
                            aria-roledescription="slide"
                            inert={!isVisible ? '' : undefined}
                            aria-hidden={!isVisible || undefined}
                            aria-labelledby={sliderId + '-headline-' + idx}
                            {...styles.slide}
                        >
                            {child}
                        </Box>
                    )
                })}
            </Flex>
        </Flex>
    )
})

Slider.propTypes = {
    children: PropTypes.node.isRequired,
    variant: PropTypes.string,
    sliderLabel: PropTypes.string,
    sliderId: PropTypes.string,
    onIndexChange: PropTypes.func
}

export default Slider
